import { useMemo, useRef, useState, type FormEvent } from "react";
import { NavLink } from "react-router-dom";
import type { CopilotConversationSummary } from "@yazamco/shared-types";
import { useClickOutside } from "../../lib/useClickOutside";
import { Icon } from "../../components/ui/Icon";
import { CopilotLogo } from "../../components/ui/CopilotLogo";

interface Props {
  conversations: CopilotConversationSummary[];
  activeId: string | null;
  isLoading: boolean;
  showArchived: boolean;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onRename: (id: string, title: string) => void;
  onTogglePin: (conversation: CopilotConversationSummary) => void;
  onToggleArchive: (conversation: CopilotConversationSummary) => void;
  onDelete: (conversation: CopilotConversationSummary) => void;
  onSetShowArchived: (show: boolean) => void;
}

type ChatFilter = "all" | "pinned" | "archived";

/** Buckets, in the order they are shown. Pinned chats leave the date groups
 *  entirely — a pinned chat is one the user wants where they can find it. */
type Bucket = "Pinned" | "Today" | "Yesterday" | "Previous 7 days" | "Older";
const BUCKET_ORDER: Bucket[] = [
  "Pinned",
  "Today",
  "Yesterday",
  "Previous 7 days",
  "Older",
];

function bucketOf(conversation: CopilotConversationSummary, now: number): Bucket {
  if (conversation.isPinned) return "Pinned";
  const days = Math.floor(
    (now - new Date(conversation.lastActivityAt).getTime()) / 86_400_000,
  );
  if (days < 1) return "Today";
  if (days < 2) return "Yesterday";
  if (days < 7) return "Previous 7 days";
  return "Older";
}

function relativeTime(iso: string, now: number): string {
  const minutes = Math.round((now - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * The chat list, drawn as the canvas's default "unified nav" layout draws it:
 * a section inside the app rail rather than a second column — a single product
 * entry paired with a prominent new-chat action, then 34px rows whose overflow
 * menu appears on hover.
 *
 * Search and the All / Pinned / Archived filter are kept from the rail form of
 * the same design (layouts A and B), because the list here is as long as the
 * user's real history rather than the canvas's five fixtures, and because both
 * are existing behaviour.
 */
export function CopilotSessionList({
  conversations,
  activeId,
  isLoading,
  showArchived,
  onSelect,
  onNewChat,
  onRename,
  onTogglePin,
  onToggleArchive,
  onDelete,
  onSetShowArchived,
}: Props): JSX.Element {
  const [term, setTerm] = useState("");
  const [filter, setFilter] = useState<ChatFilter>(showArchived ? "archived" : "all");
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [menuFor, setMenuFor] = useState<string | null>(null);

  // One `now` per render, so every row in a group is dated against the same
  // instant and the buckets cannot disagree with each other.
  const now = Date.now();
  const groups = useMemo(() => {
    const needle = term.trim().toLowerCase();
    const matching = conversations.filter((conversation) => {
      if (needle && !conversation.title.toLowerCase().includes(needle)) return false;
      if (filter === "pinned") return conversation.isPinned && !conversation.isArchived;
      if (filter === "archived") return conversation.isArchived;
      return !conversation.isArchived;
    });
    const map = new Map<Bucket, CopilotConversationSummary[]>();
    for (const conversation of matching) {
      const bucket = bucketOf(conversation, now);
      const list = map.get(bucket) ?? [];
      list.push(conversation);
      map.set(bucket, list);
    }
    return BUCKET_ORDER.filter((bucket) => map.has(bucket)).map((bucket) => ({
      bucket,
      items: map.get(bucket)!,
    }));
    // `now` is deliberately not a dependency: it changes every render and would
    // defeat the memo, while the bucket a chat falls into does not.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversations, filter, term]);

  function pickFilter(next: ChatFilter): void {
    setFilter(next);
    onSetShowArchived(next === "archived");
  }

  function submitRename(event: FormEvent): void {
    event.preventDefault();
    const title = draftTitle.trim();
    if (renamingId && title) onRename(renamingId, title);
    setRenamingId(null);
  }

  return (
    <section className="cp-chats" aria-label="Copilot chat history">
      <div className="cp-chats-head">
        <NavLink to="/copilot" className="cp-chats-product" aria-label="Microsoft 365 Copilot">
          <CopilotLogo size={20} />
          <span>Microsoft 365 Copilot</span>
        </NavLink>
        <button
          type="button"
          className="cp-new-chat"
          onClick={onNewChat}
          aria-label="New chat"
          title="New chat"
        >
          <Icon name="plus" size={19} stroke={2.2} />
        </button>
      </div>

      <div className="cp-chats-tools">
        <div className="cp-chats-search">
          <Icon name="search" size={14} />
          <input
            type="search"
            value={term}
            placeholder="Search chats"
            aria-label="Search chats"
            onChange={(event) => setTerm(event.target.value)}
          />
        </div>
        <div className="segmented cp-chats-filters" role="tablist" aria-label="Chat filter">
          {(["all", "pinned", "archived"] as ChatFilter[]).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              className="segmented-option"
              aria-selected={filter === value}
              onClick={() => pickFilter(value)}
            >
              {value[0]!.toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="cp-chats-list">
        {isLoading && <p className="cp-chats-note">Loading chats…</p>}
        {!isLoading && conversations.length === 0 && (
          <p className="cp-chats-note">No chats yet.</p>
        )}
        {!isLoading && conversations.length > 0 && groups.length === 0 && (
          <p className="cp-chats-note">Nothing matches “{term}”.</p>
        )}

        {groups.map(({ bucket, items }) => (
          <section key={bucket} className="cp-chats-group">
            <h3>{bucket}</h3>
            {items.map((conversation) => (
              <div
                key={conversation.id}
                className={`cp-chat-row${conversation.id === activeId ? " is-active" : ""}`}
              >
                {renamingId === conversation.id ? (
                  <form className="cp-chat-rename" onSubmit={submitRename}>
                    <input
                      value={draftTitle}
                      maxLength={200}
                      autoFocus
                      aria-label="Chat title"
                      onChange={(event) => setDraftTitle(event.target.value)}
                      onBlur={submitRename}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setRenamingId(null);
                      }}
                    />
                  </form>
                ) : (
                  <>
                    <button
                      type="button"
                      className="cp-chat-open"
                      onClick={() => onSelect(conversation.id)}
                    >
                      <span className="cp-chat-title">
                        {conversation.isPinned && (
                          <Icon name="pinFilled" size={11} label="Pinned" />
                        )}
                        <span>{conversation.title}</span>
                      </span>
                      <span className="cp-chat-meta">
                        {relativeTime(conversation.lastActivityAt, now)} ·{" "}
                        {conversation.turnCount}{" "}
                        {conversation.turnCount === 1 ? "turn" : "turns"}
                      </span>
                    </button>
                    <RowActions
                      conversation={conversation}
                      open={menuFor === conversation.id}
                      onOpenChange={(open) => setMenuFor(open ? conversation.id : null)}
                      onRename={() => {
                        setRenamingId(conversation.id);
                        setDraftTitle(conversation.title);
                      }}
                      onTogglePin={onTogglePin}
                      onToggleArchive={onToggleArchive}
                      onDelete={onDelete}
                    />
                  </>
                )}
              </div>
            ))}
          </section>
        ))}
      </div>
    </section>
  );
}

/**
 * One overflow menu per row, revealed on hover or keyboard focus. The
 * aria-labels name their object, so the action a menu item performs is
 * unambiguous however it is reached.
 */
function RowActions({
  conversation,
  open,
  onOpenChange,
  onRename,
  onTogglePin,
  onToggleArchive,
  onDelete,
}: {
  conversation: CopilotConversationSummary;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRename: () => void;
  onTogglePin: (conversation: CopilotConversationSummary) => void;
  onToggleArchive: (conversation: CopilotConversationSummary) => void;
  onDelete: (conversation: CopilotConversationSummary) => void;
}): JSX.Element {
  const ref = useRef<HTMLSpanElement>(null);
  useClickOutside(ref, open, () => onOpenChange(false));

  function run(action: () => void): void {
    onOpenChange(false);
    action();
  }

  return (
    <span className={`cp-chat-actions${open ? " is-open" : ""}`} ref={ref}>
      <button
        type="button"
        className="cp-chat-more"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Actions for ${conversation.title}`}
        title="Chat actions"
        onClick={() => onOpenChange(!open)}
      >
        <Icon name="dots" size={14} />
      </button>
      {open && (
        <div className="cp-chat-menu" role="menu">
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => run(onRename)}
            aria-label={`Rename ${conversation.title}`}
          >
            <Icon name="pencil" size={14} /> Rename chat
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => run(() => onTogglePin(conversation))}
            aria-label={
              conversation.isPinned
                ? `Unpin ${conversation.title}`
                : `Pin ${conversation.title}`
            }
          >
            <Icon name="pin" size={14} />
            {conversation.isPinned ? "Unpin chat" : "Pin chat"}
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={() => run(() => onToggleArchive(conversation))}
            aria-label={
              conversation.isArchived
                ? `Restore ${conversation.title}`
                : `Archive ${conversation.title}`
            }
          >
            <Icon name="archive" size={14} />
            {conversation.isArchived ? "Unarchive chat" : "Archive chat"}
          </button>
          <div className="menu-rule" />
          {/* Routed through the confirm dialog by the page. */}
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-item-danger"
            onClick={() => run(() => onDelete(conversation))}
            aria-label={`Delete ${conversation.title}`}
          >
            <Icon name="trash" size={14} /> Delete chat
          </button>
        </div>
      )}
    </span>
  );
}
